import React, { ReactNode, useEffect, useState } from 'react';

export interface SideBarModalProps {
  isOpen: boolean;
  onClose: () => void;
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  position?: 'center' | 'right';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  maxHeight?: 'screen' | 'auto';
  className?: string;
  contentRef?: React.Ref<HTMLDivElement>;
}

const SideBarModal: React.FC<SideBarModalProps> = ({
  isOpen,
  onClose,
  header,
  children,
  footer,
  width = 'md',
  position = 'center',
  showCloseButton = true,
  closeOnBackdropClick = true,
  maxHeight = 'screen',
  className = '',
  contentRef,
}) => {
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowExitConfirm(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const widthClasses = {
    sm: 'w-[400px]',
    md: 'w-[500px]',
    lg: 'w-[600px]',
    xl: 'w-[800px]',
    full: 'w-full max-w-7xl',
  };

  const positionClasses = {
    center: 'items-center justify-center',
    right: 'justify-end items-stretch',
  };

  const modalClasses = {
    center: `${widthClasses[width]} max-h-[90vh] bg-white rounded-lg shadow-2xl flex flex-col`,
    right: `${widthClasses[width]} h-full bg-white shadow-2xl flex flex-col`,
  };

  const handleRequestClose = () => {
    if (position === 'right') {
      setShowExitConfirm(true);
      return;
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!closeOnBackdropClick || e.target !== e.currentTarget) return;

    handleRequestClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        className={`fixed inset-0 z-50 flex ${positionClasses[position]}`}
        onClick={handleBackdropClick}
      >
        {/* Modal */}
        <div className={`relative ${modalClasses[position]} ${className}`}>
          {/* Header */}
          {(header || showCloseButton) && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex-1">{header}</div>
              {showCloseButton && (
                <button
                  onClick={handleRequestClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded z-10"
                  aria-label="Close modal"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div
            ref={contentRef}
            className={`flex-1 overflow-y-auto ${
              maxHeight === 'screen' ? 'max-h-full' : ''
            }`}
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="border-t border-gray-200 p-6 flex-shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>

      {showExitConfirm && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex-shrink-0">
                    <i className="ri-alert-line text-orange-500 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Are You Sure You Want To Exit?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      You have entered details into the form that are not yet
                      saved. Closing the form now will permanently lose all
                      entered data. Are you sure you want to exit?
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowExitConfirm(false);
                      onClose();
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Exit and Lose Data
                  </button>
                  <button
                    onClick={() => setShowExitConfirm(false)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    style={{ backgroundColor: '#5e2aac' }}
                  >
                    Stay and Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SideBarModal;
